import React from 'react';
import { Globe, Linkedin, Quote } from 'lucide-react'; // Ícones limpos

const LandingPage = ({ onIniciarDiagnostico, onAbrirEbook }) => {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      
      {/* --- 1. HERO SECTION --- */}
      <header className="relative bg-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 opacity-90 z-10"></div>
        <img src="/banner-bussola.png" alt="Fundo" className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-overlay" />

        <div className="relative z-20 max-w-5xl mx-auto px-6 py-24 md:py-32 flex flex-col items-center text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Descubra o seu <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">Caminho Lonjovial</span>
          </h1>
          
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mb-10 leading-relaxed">
            Uma forma consciente de atravessar o tempo. <br />
            Não se trata apenas de viver mais, mas de viver melhor e mais feliz.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-5 w-full justify-center">
            <button 
              onClick={onAbrirEbook}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-3"
            >
              <span>📖</span> Ler o WebBook
            </button>

            <button 
              onClick={onIniciarDiagnostico}
              className="bg-white hover:bg-blue-50 text-blue-900 px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-3"
            >
              <span>📊</span> Fazer meu Diagnóstico
            </button>
          </div>
        </div>
      </header>

      {/* --- 2. O CONCEITO --- */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center md:text-left flex flex-col md:flex-row gap-12 items-center">
          <div className="flex-1 space-y-6">
            <h2 className="text-3xl font-bold text-blue-900">O que é o Caminho Lonjovial?</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              O Caminho Lonjovial é uma forma consciente de atravessar melhor o tempo. 
              Ele nasce da união entre longevidade e jovialidade — palavras que, quando caminham juntas, mudam completamente a forma como enxergamos o envelhecer.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Ser lonjovial é escolher autonomia, presença e vitalidade em cada fase da vida, respeitando limites, fazendo boas escolhas e ajustando o percurso sempre que necessário.
            </p>
            <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-500 italic text-blue-800">
              "O Caminho Lonjovial não oferece fórmulas prontas nem atalhos milagrosos. Ele propõe um mapa prático, humano e possível."
            </div>
          </div>
          
          <div className="flex-1 bg-gray-900 text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-blue-500 rounded-full opacity-20"></div>
            <h3 className="text-xl font-bold mb-6 text-blue-300 uppercase tracking-widest">O Significado</h3>
            <div className="space-y-4">
              <div>
                <span className="block text-3xl font-bold text-white">Longevidade</span>
                <span className="text-gray-400 text-sm">Representa o TEMPO.</span>
              </div>
              <div className="text-2xl text-blue-500">+</div>
              <div>
                <span className="block text-3xl font-bold text-white">Jovialidade</span>
                <span className="text-gray-400 text-sm">Representa a QUALIDADE de viver esse tempo.</span>
              </div>
            </div>
            <p className="mt-8 text-gray-300 text-sm leading-relaxed border-t border-gray-700 pt-4">
              Lonjovialidade é uma nova atitude diante do envelhecer.
            </p>
          </div>
        </div>
      </section>

      {/* --- 3. O MAPA 6x6 --- */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-6">O Mapa do Caminho Lonjovial</h2>
            <p className="text-gray-600 text-lg">
              Para tornar essa jornada clara e aplicável, organizamos a vida em 6 dimensões fundamentais. 
              Um mapa que ajuda a identificar pontos fortes e oportunidades de evolução, respeitando o seu ritmo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icone: "💪", titulo: "Física", desc: "Movimento, nutrição e sono restaurador. A base da vitalidade." },
              { icone: "🧠", titulo: "Mental", desc: "Foco, aprendizado contínuo e gestão do estresse." },
              { icone: "❤️", titulo: "Emocional", desc: "Resiliência, autoestima e regulação interna." },
              { icone: "✨", titulo: "Espiritual", desc: "Propósito, valores e conexão com o todo." },
              { icone: "💰", titulo: "Financeira", desc: "Segurança, planejamento e liberdade de escolha." },
              { icone: "🤝", titulo: "Socioambiental", desc: "Relações nutritivas e ambiente saudável." },
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="text-4xl mb-4 bg-blue-50 w-16 h-16 flex items-center justify-center rounded-full">{item.icone}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{item.titulo}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 4. COMO COMEÇAR --- */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Como começar sua jornada</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            
            {/* PASSO 1: EBOOK */}
            <div 
              className="bg-emerald-50 border border-emerald-200 p-8 rounded-3xl flex flex-col justify-between cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]" 
              onClick={onAbrirEbook}
            >
              <div>
                <span className="bg-emerald-200 text-emerald-800 text-xs font-bold px-3 py-1 rounded uppercase tracking-wider">Passo 1</span>
                <h3 className="text-2xl font-bold text-emerald-900 mt-4 mb-3">WebBook Lonjovial</h3>
                <p className="text-emerald-800 leading-relaxed mb-8 text-sm">
                  Um guia digital interativo. Aprofunde-se em cada dimensão com reflexões e armadilhas comuns. O conhecimento é a base da mudança.
                </p>
              </div>
              <button 
                 className="bg-emerald-600 text-white py-4 px-6 rounded-xl font-bold w-full hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
              >
                <span>📖</span> Ler Agora
              </button>
            </div>

            {/* PASSO 2: APP */}
            <div 
              className="bg-blue-900 text-white p-8 rounded-3xl shadow-xl flex flex-col justify-between relative overflow-hidden group cursor-pointer hover:scale-[1.02] transition-all" 
              onClick={onIniciarDiagnostico}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500"></div>
              <div>
                <span className="bg-blue-700 text-xs font-bold px-3 py-1 rounded text-blue-100 uppercase tracking-wider">Passo 2</span>
                <h3 className="text-2xl font-bold mt-4 mb-3">Auto Diagnóstico</h3>
                <p className="text-blue-100 leading-relaxed mb-8 text-sm">
                  Transforme teoria em prática. Avalie como estão hoje as 6 dimensões da sua vida, visualize desequilíbrios e reconheça suas forças no gráfico radar.
                </p>
              </div>
              <button className="bg-white text-blue-900 py-4 px-6 rounded-xl font-bold w-full hover:bg-blue-50 transition-colors shadow-sm">
                Fazer Diagnóstico
              </button>
            </div>

            {/* PASSO 3: COMUNIDADE */}
            <div className="bg-indigo-50 border border-indigo-200 p-8 rounded-3xl flex flex-col justify-between hover:shadow-lg transition-all hover:scale-[1.02]">
               <div>
                 <span className="bg-indigo-200 text-indigo-800 text-xs font-bold px-3 py-1 rounded uppercase tracking-wider">Passo 3 (Em breve)</span>
                 <h3 className="text-2xl font-bold text-indigo-900 mt-4 mb-3">Comunidade SouLonjô</h3>
                 <p className="text-indigo-800 leading-relaxed mb-8 text-sm">
                   Um ambiente exclusivo para troca de experiências, desafios e suporte mútuo. Conecte-se com quem caminha na mesma direção. A força do grupo potencializa a jornada.
                 </p>
               </div>
               <button 
                 onClick={() => alert("A Comunidade SouLonjô será lançada em breve! Continue acompanhando.")}
                 className="bg-transparent border-2 border-indigo-300 text-indigo-600 py-4 px-6 rounded-xl font-bold w-full hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2"
               >
                 <span>👥</span> Aguarde Novidades
               </button>
            </div>

          </div>
        </div>
      </section>

      {/* --- 5. TESTEMUNHAIS --- */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Quem já começou a caminhada</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Insights de quem está seguindo o Caminho Lonjovial na prática.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Depoimento 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative">
              <Quote size={40} className="text-blue-100 absolute top-6 left-6" />
              <p className="text-gray-600 italic mb-6 relative z-10 pt-6">
                "O diagnóstico foi um despertar. Eu achava que estava bem só por fazer exercícios, mas vi que minha dimensão emocional estava drenando toda a minha energia. Ajustei a rota."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">M</div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Marcelo D.</p>
                  <p className="text-xs text-gray-500">42 anos • Empresário</p>
                </div>
              </div>
            </div>

            {/* Depoimento 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative">
              <Quote size={40} className="text-emerald-100 absolute top-6 left-6" />
              <p className="text-gray-600 italic mb-6 relative z-10 pt-6">
                "A ideia de 'Jovialidade' mudou minha perspectiva. Parei de lutar contra a idade e comecei a focar na vitalidade. O WebBook é leitura obrigatória!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">A</div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Ana Paula S.</p>
                  <p className="text-xs text-gray-500">56 anos • Arquiteta</p>
                </div>
              </div>
            </div>

            {/* Depoimento 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 relative">
              <Quote size={40} className="text-indigo-100 absolute top-6 left-6" />
              <p className="text-gray-600 italic mb-6 relative z-10 pt-6">
                "Simples, direto e profundo. O gráfico radar me deu a clareza visual que eu precisava para entender onde estava o desequilíbrio na minha rotina."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">R</div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Ricardo F.</p>
                  <p className="text-xs text-gray-500">38 anos • Programador</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 6. SOBRE O AUTOR (COM TEXTO ATUALIZADO E LINKS CORRIGIDOS) --- */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden flex flex-col md:flex-row">
            
            {/* Coluna da Esquerda: Bio e Foto */}
            <div className="bg-gray-50 p-8 md:p-12 md:w-1/3 flex flex-col items-center text-center border-r border-gray-100">
              <div className="relative w-32 h-32 md:w-40 md:h-40 flex-shrink-0 mb-6">
                <div className="absolute inset-0 bg-blue-100 rounded-full transform rotate-6"></div>
                <img 
                  src="/rodrigo.png" 
                  alt="Rodrigo Soares" 
                  className="relative w-full h-full object-cover rounded-full border-4 border-white shadow-lg"
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Rodrigo Soares</h2>
              <p className="text-blue-600 font-medium uppercase text-xs tracking-widest mb-6">Administrador, surfista & Triatleta</p>
              
              <div className="flex gap-4 justify-center">
                <a 
                  href="https://www.linkedin.com/in/rodrigosoares/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-white rounded-full text-gray-500 hover:text-blue-600 border border-gray-200 hover:border-blue-200 transition-colors"
                >
                  <Linkedin size={20} />
                </a>
                
                <a 
                  href="https://www.caminholonjovial.com.br/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-white rounded-full text-gray-500 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200 transition-colors"
                >
                  <Globe size={20} />
                </a>
              </div>
            </div>

            {/* Coluna da Direita: Manifesto do Autor */}
            <div className="p-8 md:p-12 md:w-2/3 flex flex-col justify-center">
              <h3 className="text-lg font-bold text-gray-400 uppercase tracking-wide mb-6 flex items-center gap-2">
                <span className="w-8 h-px bg-gray-300"></span> A Visão do Autor
              </h3>
              
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  "Eu trato a longevidade como um <strong>investimento de longo prazo</strong>. Pequenos aportes diários em bons hábitos geram rendimentos acumulados de saúde, energia e autonomia ao longo do tempo. Quando não cuidamos disso hoje, o custo aparece mais cedo ou mais tarde — geralmente quando já não temos tanta margem de escolha."
                </p>
                <p>
                  "Como administrador, aprendi que <strong>o que não se mede, não se gerencia</strong>. O Caminho Lonjovial é um sistema de gestão da vida, que ajuda você a enxergar desequilíbrios e corrigir a rota antes que eles se tornem problemas maiores."
                </p>
                <div className="bg-blue-50 p-4 rounded-xl border-l-4 border-blue-500 text-blue-900 italic font-medium">
                  "Estou aplicando essa gestão estratégica na minha própria vida. Não como teoria, mas na prática do dia a dia. Os resultados: mais saúde, energia e jovialidade, são o que me motivam a compartilhar esse caminho."
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- 7. O CONVITE --- */}
      <section className="py-20 bg-blue-50 text-center px-6">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-serif text-blue-900 italic mb-6">
            "O Caminho Lonjovial não é sobre controlar o tempo. <br/>É sobre viver melhor dentro dele."
          </h3>
          <div className="space-y-2 text-gray-600 font-medium">
            <p>Comece pelo conhecimento.</p>
            <p>Siga pela consciência.</p>
            <p>E ajuste o caminho sempre que precisar.</p>
          </div>
        </div>
      </section>

      {/* --- RODAPÉ --- */}
      <footer className="bg-white border-t border-gray-200 py-12 text-center text-sm">
        <p className="text-gray-500 font-bold mb-2">© 2025 Caminho Lonjovial</p>
        <p className="text-gray-400">Transformando longevidade em atitude.</p>
      </footer>

    </div>
  );
};

export default LandingPage;